import { ChangeDetectionStrategy, Component, computed, forwardRef, input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { QuillEditorComponent as NgxQuillEditorComponent } from 'ngx-quill';
import { QuillModules } from 'ngx-quill/config';
import { normalizeHtml } from '@ps-gui/shared-utils';

import { AbstractFormControlComponent } from '../inputs/abstract-form-control.component';

export interface QuillEditorToolbarConfig {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
}

const DEFAULT_TOOLBAR_CONFIG: Required<QuillEditorToolbarConfig> = {
  bold: true,
  italic: true,
  underline: true,
  bulletList: true,
  orderedList: true,
};

type QuillToolbarControl = string | { list: 'bullet' | 'ordered' };
type QuillToolbarGroup = QuillToolbarControl[];

@Component({
  selector: 'shared-ui-quill-editor',
  imports: [ReactiveFormsModule, NgxQuillEditorComponent],
  templateUrl: './quill-editor.component.html',
  styleUrl: './quill-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuillEditorComponent),
      multi: true,
    },
  ],
})
export class QuillEditorComponent extends AbstractFormControlComponent<string> {
  readonly placeholder = input('');
  readonly toolbar = input<QuillEditorToolbarConfig>(DEFAULT_TOOLBAR_CONFIG);
  readonly normalizeEmptyContent = input(true);

  readonly quillModules = computed<QuillModules>(() => ({
    toolbar: this.toolbarGroups(),
  }));

  readonly quillFormats = computed(() => {
    const toolbar = this.mergedToolbarConfig();
    const formats: string[] = [];

    if (toolbar.bold) formats.push('bold');
    if (toolbar.italic) formats.push('italic');
    if (toolbar.underline) formats.push('underline');
    if (toolbar.bulletList || toolbar.orderedList) formats.push('list');

    return formats;
  });

  override onInternalControlValueChange(value: string | null): void {
    this.onChange(this.normalizeValue(value));
    this.onTouched();
  }

  private normalizeValue(value: string | null | undefined): string | null {
    return this.normalizeEmptyContent() ? normalizeHtml(value) : (value ?? null);
  }

  private toolbarGroups(): QuillToolbarGroup[] {
    const toolbar = this.mergedToolbarConfig();
    const textFormattingGroup: QuillToolbarGroup = [];
    const listFormattingGroup: QuillToolbarGroup = [];

    if (toolbar.bold) textFormattingGroup.push('bold');
    if (toolbar.italic) textFormattingGroup.push('italic');
    if (toolbar.underline) textFormattingGroup.push('underline');
    if (toolbar.bulletList) listFormattingGroup.push({ list: 'bullet' });
    if (toolbar.orderedList) listFormattingGroup.push({ list: 'ordered' });

    return [textFormattingGroup, listFormattingGroup].filter((group) => group.length > 0);
  }

  private mergedToolbarConfig(): Required<QuillEditorToolbarConfig> {
    return {
      ...DEFAULT_TOOLBAR_CONFIG,
      ...this.toolbar(),
    };
  }
}
